import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

function ActivationCodeModal({ isOpen, onClose, onActivated }) {
  const { token } = useAuth();

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [telegramLoading, setTelegramLoading] = useState(false);
  const [error, setError] = useState("");
  const [telegramError, setTelegramError] = useState("");
  const [telegramSuccess, setTelegramSuccess] = useState("");

  const [successData, setSuccessData] = useState(null);

  const getAuthToken = () => {
    return (
      token ||
      localStorage.getItem("token") ||
      localStorage.getItem("access_token")
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";

    try {
      return new Date(value).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return value;
    }
  };

  const isPremiumActive = () => {
    if (!successData) return false;

    const plan = String(successData.plan || "").toLowerCase();
    const premiumUntil = successData.premium_until;

    if (plan !== "premium") return false;
    if (!premiumUntil) return false;

    return new Date(premiumUntil) > new Date();
  };

  const formatActivationCode = (value) => {
    const cleaned = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 16);

    const parts = [];

    if (cleaned.length > 0) parts.push(cleaned.slice(0, 2));
    if (cleaned.length > 2) parts.push(cleaned.slice(2, 4));
    if (cleaned.length > 4) parts.push(cleaned.slice(4, 8));
    if (cleaned.length > 8) parts.push(cleaned.slice(8, 12));
    if (cleaned.length > 12) parts.push(cleaned.slice(12, 16));

    return parts.join("-");
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchLicenseStatus = async () => {
      setCheckingStatus(true);
      setError("");
      setTelegramError("");
      setTelegramSuccess("");

      try {
        const authToken = getAuthToken();

        if (!authToken) {
          return;
        }

        const res = await fetch(`${API_BASE}/license/status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const data = await res.json();

        if (!res.ok) {
          return;
        }

        const plan = String(data?.plan || "").toLowerCase();
        const premiumUntil = data?.premium_until;

        const premiumActive =
          plan === "premium" &&
          premiumUntil &&
          new Date(premiumUntil) > new Date();

        if (premiumActive) {
          setSuccessData({
            plan: "premium",
            premium_until: premiumUntil,
            duration_months: data?.duration_months,
            duration_days: data?.duration_days,
          });
        } else {
          setSuccessData(null);
        }
      } catch (err) {
        console.warn("Gagal cek license status:", err);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchLicenseStatus();
  }, [isOpen, token]);

  const handleActivate = async (e) => {
    e.preventDefault();

    const cleanCode = formatActivationCode(code);

    if (!cleanCode) {
      setError("Kode aktivasi wajib diisi.");
      return;
    }

    setLoading(true);
    setError("");
    setTelegramError("");
    setTelegramSuccess("");
    setSuccessData(null);

    try {
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error("Session login tidak ditemukan. Silakan login ulang.");
      }

      const res = await fetch(`${API_BASE}/license/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          code: cleanCode,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Aktivasi kode gagal.");
      }

      const normalizedData = {
        ...data,
        plan: String(data?.plan || "premium").toLowerCase(),
      };

      setSuccessData(normalizedData);
      setCode("");

      if (typeof onActivated === "function") {
        onActivated(normalizedData);
      }
    } catch (err) {
      setError(err.message || "Terjadi kesalahan saat aktivasi kode.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectTelegram = async () => {
    setTelegramLoading(true);
    setTelegramError("");
    setTelegramSuccess("");

    try {
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error("Session login tidak ditemukan. Silakan login ulang.");
      }

      if (!isPremiumActive()) {
        throw new Error("Connect Telegram hanya tersedia untuk user Premium aktif.");
      }

      const res = await fetch(`${API_BASE}/telegram/connect-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.detail || "Gagal membuat koneksi Telegram.");
      }

      if (!data?.telegram_url) {
        throw new Error("Link Telegram tidak ditemukan dari server.");
      }

      setTelegramSuccess(
        "Link Telegram berhasil dibuat. Silakan klik Start di Telegram untuk menyambungkan akun."
      );

      window.open(data.telegram_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setTelegramError(err.message || "Terjadi kesalahan saat connect Telegram.");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const formatted = formatActivationCode(e.target.value);
    setCode(formatted);
    if (error) setError("");
  };

  if (!isOpen) return null;

  const premiumActive = isPremiumActive();

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-md">
      <div className="relative w-full max-w-md rounded-[28px] border border-white/10 bg-slate-900 p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-slate-500 transition hover:text-white"
          aria-label="Tutup aktivasi kode"
        >
          ✕
        </button>

        <div className="mb-6">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-500/20 ring-1 ring-indigo-400/30">
            <span className="text-lg font-bold text-indigo-300">SV</span>
          </div>

          <h2 className="text-2xl font-bold text-white">Aktivasi Kode</h2>
          <p className="mt-2 text-sm leading-6 text-slate-400">
            Masukkan kode aktivasi dari pembelian Anda. Kode hanya dapat digunakan
            untuk email Google yang terdaftar saat pembelian.
          </p>
        </div>

        {checkingStatus ? (
          <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4 text-sm text-slate-300">
            Mengecek status Premium...
          </div>
        ) : successData ? (
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
            <div className="text-sm font-semibold text-emerald-300">
              {premiumActive ? "Premium aktif" : "Aktivasi berhasil"}
            </div>

            <div className="mt-2 text-sm leading-6 text-slate-300">
              Akun Anda sekarang aktif sebagai{" "}
              <span className="font-semibold text-white">
                {successData.plan || "premium"}
              </span>
              .
            </div>

            <div className="mt-3 rounded-xl bg-slate-950/40 p-3 text-xs text-slate-300">
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Paket</span>
                <span className="font-medium text-white">
                  {successData.duration_months
                    ? `${successData.duration_months} bulan`
                    : successData.duration_days
                    ? `${successData.duration_days} hari`
                    : "Premium"}
                </span>
              </div>

              <div className="mt-2 flex justify-between gap-4">
                <span className="text-slate-500">Aktif sampai</span>
                <span className="font-medium text-white">
                  {formatDate(successData.premium_until)}
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-sky-500/20 bg-sky-500/10 p-4">
              <div className="text-sm font-semibold text-sky-200">
                Telegram Watchlist Insight
              </div>

              <p className="mt-2 text-xs leading-5 text-slate-300">
                Hubungkan Telegram untuk menerima Watchlist Insight otomatis
                setiap hari bursa jam 07.00 WIB.
              </p>

              {telegramError && (
                <div className="mt-3 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-xs text-rose-300">
                  {telegramError}
                </div>
              )}

              {telegramSuccess && (
                <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs text-emerald-300">
                  {telegramSuccess}
                </div>
              )}

              <button
                type="button"
                onClick={handleConnectTelegram}
                disabled={!premiumActive || telegramLoading}
                className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white transition ${
                  !premiumActive || telegramLoading
                    ? "cursor-not-allowed bg-slate-700 text-slate-400"
                    : "bg-sky-500 hover:bg-sky-400"
                }`}
              >
                {telegramLoading
                  ? "Membuat koneksi..."
                  : premiumActive
                  ? "Connect Telegram"
                  : "Connect Telegram 🔒"}
              </button>

              {!premiumActive && (
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Fitur ini hanya tersedia untuk user Premium aktif.
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full rounded-2xl bg-indigo-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-indigo-400"
            >
              Tutup
            </button>
          </div>
        ) : (
          <form onSubmit={handleActivate}>
            <label className="block text-left text-sm font-medium text-slate-300">
              Kode Aktivasi
            </label>

            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="Contoh: SV-PM-K8F3-MN7Q-ZP2X"
              maxLength={20}
              className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white outline-none transition placeholder:text-slate-600 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20"
              autoFocus
            />

            {error && (
              <div className="mt-4 rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-300">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`mt-5 w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white transition ${
                loading
                  ? "cursor-wait bg-indigo-500/60"
                  : "bg-indigo-500 hover:bg-indigo-400"
              }`}
            >
              {loading ? "Memproses..." : "Aktifkan Kode"}
            </button>

            <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <div className="text-sm font-semibold text-slate-300">
                Connect Telegram 🔒
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                Aktifkan Premium terlebih dahulu untuk menerima Watchlist Insight
                otomatis setiap pagi sebelum market buka.
              </p>
            </div>

            <p className="mt-4 text-xs leading-5 text-slate-500">
              Pastikan Anda login menggunakan email Google yang sama dengan email
              yang didaftarkan saat pembelian.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default ActivationCodeModal;
